import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTE } from '@/constants';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import { useSession } from '@/states/common';
import { IPlan } from '@/components/subscription-change-modal/constant';

export const usePlanRecommendation = () => {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const paymentPlans = session?.paymentPlans;
  const selectedClient = session.client;

  const {
    limits,
    current,
    exceeded,
    isOverLimit,
    planName: currentPlan,
  } = usePlanLimits();

  // Get yearly plans for pricing info
  const yearlyPlans = useMemo(() => paymentPlans?.year ?? [], [paymentPlans]);

  // Find Professional and Team plan data
  const professionalPlan = useMemo(
    () => yearlyPlans.find((p: IPlan) => p.plan === 'Professional'),
    [yearlyPlans]
  );

  const teamPlan = useMemo(
    () => yearlyPlans.find((p: IPlan) => p.plan === 'Team'),
    [yearlyPlans]
  );

  const datasourceCurrent = current.datasources;
  const workspaceCurrent = current.workspaces;
  const userCurrent = current.users;

  // Determine suggested upgrade plan based on current plan and what fits their usage
  const suggestedPlan = useMemo(():
    | 'professional'
    | 'team'
    | 'contact_support' => {
    const normalizedCurrentPlan = currentPlan.toLowerCase();

    // If user is on Professional plan, always recommend Team (next tier up)
    if (normalizedCurrentPlan === 'professional') {
      return 'team';
    }

    // If user is already on Team plan and still exceeding, they need enterprise
    if (normalizedCurrentPlan === 'team' && isOverLimit) {
      return 'contact_support';
    }

    // Check if current usage would fit in Team plan
    if (!teamPlan) return 'professional';
    const teamLimits = teamPlan.requirements;
    const fitsInTeam =
      (teamLimits.datasources === 'unlimited' ||
        datasourceCurrent <= teamLimits.datasources) &&
      (teamLimits.workspace === 'unlimited' ||
        workspaceCurrent <= teamLimits.workspace) &&
      (teamLimits.user === 'unlimited' || userCurrent <= teamLimits.user);

    if (!fitsInTeam) {
      // Current usage exceeds Team plan limits
      return 'contact_support';
    }

    // Check if current usage would fit in Professional plan
    if (!professionalPlan) return 'team';
    const professionalLimits = professionalPlan.requirements;
    const fitsInProfessional =
      (professionalLimits.datasources === 'unlimited' ||
        datasourceCurrent <= professionalLimits.datasources) &&
      (professionalLimits.workspace === 'unlimited' ||
        workspaceCurrent <= professionalLimits.workspace) &&
      (professionalLimits.user === 'unlimited' ||
        userCurrent <= professionalLimits.user);

    if (!fitsInProfessional) {
      // Needs Team plan
      return 'team';
    }

    // Professional plan is sufficient
    return 'professional';
  }, [
    currentPlan,
    isOverLimit,
    teamPlan,
    professionalPlan,
    datasourceCurrent,
    workspaceCurrent,
    userCurrent,
  ]);

  // Get plan config based on suggested plan
  const planConfig = useMemo(() => {
    if (suggestedPlan === 'contact_support') {
      return null;
    }

    const plan = suggestedPlan === 'professional' ? professionalPlan : teamPlan;
    if (!plan) return null;

    return {
      title: plan.plan,
      price: plan.price.integer,
      requirements: plan.requirements,
    };
  }, [suggestedPlan, professionalPlan, teamPlan]);

  // Get exceeded resources count
  const exceededCount = useMemo(() => {
    let count = 0;
    if (exceeded.workspaces) count += 1;
    if (exceeded.datasources) count += 1;
    if (exceeded.users) count += 1;
    return count;
  }, [exceeded]);

  // Get primary exceeded resource for messaging
  const primaryResource = useMemo(() => {
    if (exceeded.workspaces) return 'workspaces';
    if (exceeded.datasources) return 'datasources';
    return 'users';
  }, [exceeded]);

  const handleUpgrade = () => {
    navigate(
      `/ws/${selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}?plan=${suggestedPlan}`
    );
  };

  const handleContactSupport = () => {
    window.open('https://support.viewmetrics.com/en/', '_blank');
  };

  const handleManageResources = () => {
    if (exceeded.datasources) {
      navigate(
        `/ws/${selectedClient?.id}/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}`
      );
    } else if (exceeded.workspaces) {
      navigate(
        `/ws/${selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.WORKSPACE}`
      );
    } else if (exceeded.users) {
      navigate(
        `/ws/${selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.TEAM}`
      );
    }
  };

  const billingPageLink = `/ws/${selectedClient?.id}/${APP_ROUTE.SETTINGS}/${APP_ROUTE.BILLING}?plan=${suggestedPlan}`;

  return {
    suggestedPlan,
    planConfig,
    exceededCount,
    primaryResource,
    handleUpgrade,
    handleContactSupport,
    handleManageResources,
    limits,
    current,
    exceeded,
    isOverLimit,
    billingPageLink,
    teamPlan,
    professionalPlan,
  };
};
